import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Select } from "../../components/ui-modal";
import { INVOICE_STATUS, PAYMENT_STATUS, Badge, eur, money, fmtDate } from "../../lib/helpers";
import * as Icons from "lucide-react";
import AuthFileLink from "../../components/AuthFileLink";

const MONTHS = [
  { v: "1", l: "Janvier" }, { v: "2", l: "Février" }, { v: "3", l: "Mars" }, { v: "4", l: "Avril" },
  { v: "5", l: "Mai" }, { v: "6", l: "Juin" }, { v: "7", l: "Juillet" }, { v: "8", l: "Août" },
  { v: "9", l: "Septembre" }, { v: "10", l: "Octobre" }, { v: "11", l: "Novembre" }, { v: "12", l: "Décembre" },
];

export default function AdminInvoices() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [f, setF] = useState({ client: "all", nature: "all", status: "all", currency: "all", month: "all", year: "all", q: "" });

  useEffect(() => { api.get("/admin/invoices").then((r) => setData(r.data)).catch(() => {}); }, []);

  const invoices = useMemo(() => data?.invoices || [], [data]);
  const curSym = data?.currencies || { EUR: "€", USD: "$", AED: "AED" };
  const years = useMemo(() => Array.from(new Set(invoices.map((i) => (i.due_date || "").slice(0, 4)).filter(Boolean))).sort().reverse(), [invoices]);

  const filtered = useMemo(() => invoices.filter((i) => {
    if (f.client !== "all" && i.client_id !== f.client) return false;
    if (f.nature !== "all" && (i.client_nature || "") !== f.nature) return false;
    if (f.status !== "all" && i.invoice_status !== f.status) return false;
    if (f.currency !== "all" && i.currency !== f.currency) return false;
    const [y, m] = (i.due_date || "").split("-");
    if (f.year !== "all" && y !== f.year) return false;
    if (f.month !== "all" && String(Number(m)) !== f.month) return false;
    if (f.q) {
      const hay = `${i.invoice_number || ""} ${i.project_name || ""} ${i.client_name || ""} ${i.label || ""}`.toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    return true;
  }), [invoices, f]);

  const exportCsv = () => {
    const head = ["N° facture", "Entreprise", "Projet", "Libellé", "Devise", "Montant", "Équivalent €", "Date échéance", "Date paiement", "Statut facture", "Statut paiement", "Fichier"];
    const rows = filtered.map((i) => [
      i.invoice_number || "", i.client_name || "", i.project_name || "", i.label || "", i.currency,
      i.amount, i.converted ? i.amount_eur : "", i.due_date || "", i.paid_date || "",
      INVOICE_STATUS[i.invoice_status]?.label || i.invoice_status,
      PAYMENT_STATUS[i.payment_status]?.label || i.payment_status, i.invoice_name || "",
    ]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `factures_clients_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const reset = () => setF({ client: "all", nature: "all", status: "all", currency: "all", month: "all", year: "all", q: "" });
  const money2 = (n, c) => (Number(n) || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " " + (curSym[c] || c || "€");

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageTitle overline="Comptabilité" title="Factures clients" />
        <button onClick={exportCsv} data-testid="invoices-export" className="mt-1 inline-flex items-center gap-2 bg-white border border-marine/25 text-marine rounded-sm px-4 py-2 text-sm hover:bg-marine-50 transition-colors">
          <Icons.Download size={16} /> Exporter CSV
        </button>
      </div>
      <p className="text-sm text-marine/80 -mt-4 mb-5">Historique consultable de toutes les factures émises (échéances avec fichier de facture joint).</p>

      {/* Filtres */}
      <div className="bg-sable border border-marine/10 rounded-sm p-4 mb-5 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="invoices-filters">
        <div className="md:col-span-2 relative">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muet" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="invoices-search" placeholder="Rechercher (n° facture, projet, entreprise…)" className="inp !pl-9" />
        </div>
        <Select value={f.client} onChange={(v) => setF({ ...f, client: v })} testid="filter-client" ariaLabel="Filtrer par entreprise"
          options={[{ v: "all", l: "Toutes les entreprises" }, ...(data?.clients || []).map((c) => ({ v: c.id, l: c.name }))]} />
        <Select value={f.nature} onChange={(v) => setF({ ...f, nature: v })} testid="filter-nature" ariaLabel="Filtrer par type"
          options={[{ v: "all", l: "Tous les types" }, ...(data?.natures || []).map((n) => ({ v: n, l: n }))]} />
        <Select value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="filter-status" ariaLabel="Filtrer par statut"
          options={[{ v: "all", l: "Tous les statuts" }, ...Object.entries(INVOICE_STATUS).map(([k, s]) => ({ v: k, l: s.label }))]} />
        <Select value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testid="filter-currency" ariaLabel="Filtrer par devise"
          options={[{ v: "all", l: "Toutes devises" }, ...Object.keys(curSym).map((c) => ({ v: c, l: c }))]} />
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.month} onChange={(v) => setF({ ...f, month: v })} testid="filter-month" ariaLabel="Filtrer par mois"
            options={[{ v: "all", l: "Mois" }, ...MONTHS]} />
          <Select value={f.year} onChange={(v) => setF({ ...f, year: v })} testid="filter-year" ariaLabel="Filtrer par année"
            options={[{ v: "all", l: "Année" }, ...years.map((y) => ({ v: y, l: y }))]} />
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-marine/80" data-testid="invoices-count">{filtered.length} facture{filtered.length > 1 ? "s" : ""}</span>
        <button onClick={reset} className="text-sm text-bordeaux hover:underline" data-testid="invoices-reset">Réinitialiser les filtres</button>
      </div>

      {/* Table */}
      <div className="bg-sable border border-marine/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead>
            <tr className="text-left text-marine/80 border-b border-marine/10 bg-sable">
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">N° / Libellé</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">Entreprise</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">Projet</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-right">Montant</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-right">Équiv. €</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">Échéance</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">Facture</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-left">Paiement</th>
              <th className="px-4 py-3 text-xs uppercase tracking-wide font-medium text-center">Fichier</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-marine/80">Aucune facture ne correspond aux filtres.</td></tr>
            ) : filtered.map((i) => (
              <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="border-b border-marine/5 last:border-0 bg-creme hover:bg-sable transition-colors cursor-pointer"
                onClick={() => nav(`/admin/projets/${i.project_id}?tab=facturation`)}>
                <td className="px-4 py-3">
                  <div className="font-medium text-marine">{i.invoice_number || "—"}</div>
                  <div className="text-xs text-marine/80">{i.label}</div>
                </td>
                <td className="px-4 py-3 text-marine/80">{i.client_name || "—"}
                  {i.billing_contact && <div className="text-xs text-bleu flex items-center gap-1 mt-0.5" data-testid={`invoice-billing-${i.id}`}><Icons.Calculator size={16} /> {i.billing_contact.name}</div>}
                </td>
                <td className="px-4 py-3 text-marine/80">{i.project_name}<div className="text-xs text-marine/80">{i.type_label}</div></td>
                <td className="px-4 py-3 text-right font-medium text-marine whitespace-nowrap">{money2(i.amount, i.currency)}</td>
                <td className="px-4 py-3 text-right text-marine/80 whitespace-nowrap" data-testid={`invoice-eur-${i.id}`}>
                  {i.currency === "EUR" ? "—" : (i.converted ? eur(i.amount_eur) : <span className="text-rouge text-xs">taux manquant</span>)}
                </td>
                <td className="px-4 py-3 text-marine/80 whitespace-nowrap">{fmtDate(i.due_date)}</td>
                <td className="px-4 py-3"><Badge map={INVOICE_STATUS} status={i.invoice_status} /></td>
                <td className="px-4 py-3"><Badge map={PAYMENT_STATUS} status={i.payment_status} /></td>
                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  {i.invoice_url ? (
                    <AuthFileLink path={i.invoice_url} data-testid={`invoice-download-${i.id}`}
                      title={i.invoice_name || "Télécharger la facture"}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-sm text-marine hover:bg-marine-50 transition-colors">
                      <Icons.Download size={16} />
                    </AuthFileLink>
                  ) : <span className="text-muet">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
